// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Post-login onboarding  (delivery location step)
// Place search and reverse geocoding (OpenStreetMap Nominatim — free, no key, light use only:
// a search only on submit, one reverse lookup per settled pin) and the serviceability check
// against the site's own service zones (data/operations.ts, the same data as the Locations map).
// Zones are illustrative boxes around each city centre, not surveyed boundaries.
// ─────────────────────────────────────────────────────────────────────────────
import { SERVICE_ZONES, type Coordinates, type ServiceZone } from "../data/operations";

export interface Place {
  lat: number;
  lng: number;
  /** One-line label for a search result. */
  label: string;
  house: string;
  street: string;
  city: string;
  state: string;
  pincode: string;
}

interface NominatimAddress {
  house_number?: string;
  building?: string;
  road?: string;
  neighbourhood?: string;
  suburb?: string;
  city_district?: string;
  city?: string;
  town?: string;
  village?: string;
  county?: string;
  state_district?: string;
  state?: string;
  postcode?: string;
}

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  address?: NominatimAddress;
}

const NOMINATIM = "https://nominatim.openstreetmap.org";

function toPlace(result: NominatimResult): Place {
  const a = result.address ?? {};
  const street = [a.road, a.neighbourhood ?? a.suburb ?? a.city_district].filter(Boolean).join(", ");
  return {
    lat: Number(result.lat),
    lng: Number(result.lon),
    label: result.display_name,
    house: [a.building, a.house_number].filter(Boolean).join(" "),
    street,
    city: a.city ?? a.town ?? a.village ?? a.state_district ?? a.county ?? "",
    state: a.state ?? "",
    pincode: (a.postcode ?? "").replace(/\s/g, ""),
  };
}

async function nominatim<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${NOMINATIM}${path}`, { signal, headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`Nominatim ${response.status}`);
  return (await response.json()) as T;
}

/** Up to 5 places in India matching the text. Rejects on network failure. */
export async function searchPlaces(query: string, signal?: AbortSignal): Promise<Place[]> {
  const params = new URLSearchParams({ format: "jsonv2", addressdetails: "1", countrycodes: "in", limit: "5", q: query });
  const results = await nominatim<NominatimResult[]>(`/search?${params}`, signal);
  return results.map(toPlace);
}

/** The address at a point, or null when the lookup has nothing for it. Rejects on network failure. */
export async function reversePlace(lat: number, lng: number, signal?: AbortSignal): Promise<Place | null> {
  const params = new URLSearchParams({ format: "jsonv2", addressdetails: "1", zoom: "18", lat: String(lat), lon: String(lng) });
  const result = await nominatim<NominatimResult & { error?: string }>(`/reverse?${params}`, signal);
  return result.error ? null : toPlace(result);
}

/** Ray-casting point-in-polygon. */
function inside(point: Coordinates, polygon: Coordinates[]): boolean {
  let hit = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    const crosses = a.lat > point.lat !== b.lat > point.lat && point.lng < ((b.lng - a.lng) * (point.lat - a.lat)) / (b.lat - a.lat) + a.lng;
    if (crosses) hit = !hit;
  }
  return hit;
}

/** The service zone containing the point, if any (any availability, so the UI can say "coming soon"). */
export function findServiceZone(lat: number, lng: number): ServiceZone | null {
  return SERVICE_ZONES.find((zone) => inside({ lat, lng }, zone.boundary)) ?? null;
}
